/**
 * The Three.js implementation of `WorldRenderer`.
 *
 * Everything engine-specific in the project lives in this directory. The core
 * hands over typed arrays and an ENU position; this turns them into scene
 * objects and nothing more.
 */

import type {
  Scene,
  InstancedMesh} from 'three';
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DataTexture,
  Matrix4,
  Vector3,
  Group,
  InstancedMesh as InstancedMeshClass,
  type Material,
  NearestFilter,
  type Object3D,
  Mesh,
  RedFormat,
  UnsignedByteType,
} from 'three';
import type { TerrainLandcover } from './terrainMaterial.js';
import { PORTAL_SLOTS, createTerrainMaterial, terrainPortals } from './terrainMaterial.js';
import type { PlacedVectorChunk } from '../core/build/placement.js';
import type { TerrainMeshData } from '../core/build/terrainmesh.js';
import type { GeoOrigin } from '../core/geo/enu.js';
import type { TileKey } from '../core/geo/tilekey.js';
import { tileExtent, tileKeyToString } from '../core/geo/tilekey.js';
import type { WorldRenderer } from '../core/renderer.js';
import { annotateFootpaths, crossingPoints, extractFootpaths } from '../core/character/footpaths.js';
import { zebraMarks } from '../core/build/zebras.js';
import type { SurfaceMesh } from '../core/build/surfaceindex.js';
import { SurfaceIndex } from '../core/build/surfaceindex.js';
import type { Portal } from '../core/build/tunnelmesh.js';
import { inMouth } from '../core/build/tunnelmesh.js';
import { CollisionIndex } from '../core/build/colliders.js';
import { FootpathNetwork } from './character/villagers.js';
import { extractLanes } from '../core/traffic/lanes.js';
import { LaneNetwork } from './traffic.js';
import { TrackNetwork } from './tracks.js';
import { RailCrossings } from './railCrossings.js';
import { ROAD_STYLE } from '../core/sources/shortbread.js';
import type { VegetationChunk } from './vegetation.js';
import { buildVegetation, disposeVegetation } from './vegetation.js';
import { updateLampLightMap } from './flora.js';
import type { Species } from '../core/biome/flora.js';
import { SPECIES, SPECIES_ID, VEHICLES } from '../core/biome/flora.js';
import type { PlacedHelipad } from '../core/build/helipads.js';
import { helipadDiscs } from '../core/build/helipads.js';
import type { EnergySpot, Source } from '../core/economy/energy.js';
import type { VehicleSpecies } from '../core/build/parking.js';
import { SEMI_TRAILER_BOX } from '../core/build/parking.js';
import { tankOf } from './fleet.js';
import { exhaustGeometry, exhaustMaterial, exhaustSpot } from './Vehicle.js';
import type { CarKind } from './Vehicle.js';
import { GROUND_LAYER } from './layers.js';

/** The car body each parked car species is, for its tailpipe. Trucks' exhausts are part of them. */
const CAR_KIND: Partial<Record<Species, CarKind>> = {
  carHatch: 'hatch', carSedan: 'sedan', carEstate: 'estate', carSuv: 'suv', carVan: 'van', carPickup: 'pickup',
};

/** A railway station or halt standing in the world, scene metres, as `railStationsNear` lists it. */
export interface RailStation {
  x: number;
  z: number;
  name: string;
  /** A halt: only the stopping trains call. */
  halt: boolean;
}

/** A helipad standing in the world, as `helipads` lists it. */
export interface HelipadRef {
  /** Stable across rebuilds of its tile: the tile and its place in it. */
  id: string;
  /** Scene position of its middle, on its surface, and its heading. */
  x: number;
  y: number;
  z: number;
  yaw: number;
  radius: number;
  name: string;
}

/** A prop standing in the world, as `propsNear` finds it: which, where (scene metres), and how far off. */
export interface PropRef {
  chunk: string;
  mesh: InstancedMesh;
  index: number;
  species: Species;
  x: number;
  y: number;
  z: number;
  d: number;
}

/** What was done to a prop, in its tile's frame, to do again when the tile is rebuilt. */
interface PropChange {
  x: number;
  z: number;
  species: number;
  how: 'gone' | 'flat' | 'bent';
  push: { x: number; z: number };
  angle: number;
  /** How far from where it stands its colliders reach, metres. */
  reach: number;
}

/**
 * Fold away the faces of a bus stop's name board in a tile's `signs`: the
 * board of a shelter whose foot is at (x, y, z) in the tile's frame, hung a
 * little in front of it at the roof's edge, 2.36 m up. Each face's corners
 * before (tile frame) and its patch of the atlas.
 */
function hideStopBoard(signs: Mesh | undefined, x: number, y: number, z: number): Array<{ corners: Vector3[]; uvs: number[] }> {
  if (signs === undefined) return [];
  const pos = signs.geometry.getAttribute('position') as BufferAttribute;
  const uv = signs.geometry.getAttribute('uv') as BufferAttribute;
  const out: Array<{ corners: Vector3[]; uvs: number[] }> = [];
  for (let f = 0; f < pos.count / 4; f++) {
    let cx = 0, cy = 0, cz = 0;
    for (let k = 0; k < 4; k++) { cx += pos.getX(f * 4 + k) / 4; cy += pos.getY(f * 4 + k) / 4; cz += pos.getZ(f * 4 + k) / 4; }
    if (Math.hypot(cx - x, cz - z) > 1.4 || Math.abs(cy - (y + 2.36)) > 0.45) continue;
    const corners: Vector3[] = [], uvs: number[] = [];
    for (let k = 0; k < 4; k++) {
      corners.push(new Vector3(pos.getX(f * 4 + k), pos.getY(f * 4 + k), pos.getZ(f * 4 + k)));
      uvs.push(uv.getX(f * 4 + k), uv.getY(f * 4 + k));
    }
    // Already folded away: all four corners at one point.
    if (corners[0].distanceTo(corners[2]) < 1e-4) continue;
    out.push({ corners, uvs });
    for (let k = 1; k < 4; k++) pos.setXYZ(f * 4 + k, corners[0].x, corners[0].y, corners[0].z);
  }
  if (out.length > 0) pos.needsUpdate = true;
  return out;
}

const bendAxis = new Vector3();
const bendTurn = new Matrix4();
const bendSquash = new Matrix4();

/** A parked vehicle standing in the world, as `nearestParked` finds it. */
export interface ParkedRef {
  chunk: string;
  mesh: InstancedMesh;
  index: number;
  /** Scene position of its centre on the ground, and its yaw. */
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** How it is tipped to the slope it stands on, radians: nose up, right side up. */
  pitch: number;
  roll: number;
  species: Species;
  /** Body colour, 0xRRGGBB. */
  paint: number;
}

const ZERO = new Matrix4().makeScale(0, 0, 0);
const scratchMatrix = new Matrix4();
const scratchColour = new Color();

interface Chunk {
  /** The tile's terrain: one mesh, or a group of pieces (`TERRAIN_PIECES`). */
  mesh: Object3D;
  /** Its meshes: the mesh itself, or the group's pieces. */
  pieces: Mesh[];
  key: TileKey;
  width: number;
  depth: number;
  /** A material of its own, when it wears a landcover raster. */
  own?: Material;
  /** The mesh's own index, kept when the geometry was expanded to flat facets. */
  expandedFrom?: ArrayLike<number>;
  /** The whole facet arrays the pieces are views of, when built from the worker's. */
  facets?: { positions: Float32Array; normals: Float32Array };
}

/** Pieces a near terrain tile is drawn in, for its upload to spread over frames. */
const TERRAIN_PIECES = 4;

interface VectorChunk {
  group: Group;
  parts: VegetationChunk;
  key: TileKey;
  helipads: PlacedHelipad[];
  energy: EnergySpot[];
  stations: Array<{ x: number; z: number; name: string }>;
  railStations: Array<{ x: number; z: number; name: string; halt: boolean }>;
  garages: Array<{ x: number; z: number; faceX: number; faceZ: number; span: number }>;
}

/** How the renderer spends a device's GPU. See `Graphics` in config.ts. */
export interface RendererOptions {
  /**
   * Whether the small things — lamps, fences, bins, parked cars, shrubs —
   * cast shadows too, or only trees and buildings: a phone draws the
   * shadow-casters a second time every frame, and those are most of them.
   */
  propShadows?: boolean;
  /** Bytes of new GPU buffers shown a frame; undefined shows a tile whole. */
  uploadBudget?: number;
  /**
   * The near terrain drawn as separate facets, each its own shade: three
   * times the vertices, and a conversion on the main thread per tile.
   */
  facetTerrain?: boolean;
}

/** A tile's surfaces to stand on, indexed when first needed. */
/** How far under the feet a tunnel's floor is still taken for the ground, metres: a step down, not the floor under the hill. */
const TUNNEL_REACH = 2.5;

interface Walkable {
  group: Group;
  width: number;
  depth: number;
  meshes: SurfaceMesh[];
  index?: SurfaceIndex;
  /** Tunnel floors, where the terrain over them is not the ground, and the portals' mouths. */
  tunnel?: SurfaceMesh;
  tunnelIndex?: SurfaceIndex;
  roof?: SurfaceMesh;
  roofIndex?: SurfaceIndex;
  /** Buildings' roofs and the pads on them, apart from roads and decks: see `rooftopHeightAt`. */
  rooftops: SurfaceMesh[];
  rooftopIndex?: SurfaceIndex;
  portals: readonly Portal[];
}

/** Roughly how many bytes an object's buffers take on the GPU. */
function gpuBytes(object: Object3D): number {
  let bytes = 0;
  object.traverse((o) => {
    const g = (o as Mesh).geometry as BufferGeometry | undefined;
    if (g === undefined) return;
    for (const a of Object.values(g.attributes)) bytes += (a.array as ArrayLike<number> & { byteLength: number }).byteLength;
    if (g.index !== null) bytes += (g.index.array as unknown as { byteLength: number }).byteLength;
    // And its texture, read back from a canvas and uploaded: a tile's sign atlas is megabytes.
    const image = ((o as Mesh).material as { map?: { image?: { width?: number; height?: number } } } | undefined)?.map?.image;
    if (image?.width !== undefined && image.height !== undefined) bytes += image.width * image.height * 4;
    const inst = o as InstancedMesh;
    if (inst.isInstancedMesh === true) bytes += inst.instanceMatrix.array.byteLength + (inst.instanceColor?.array.byteLength ?? 0);
  });
  return bytes;
}

export class ThreeRenderer implements WorldRenderer {
  private readonly scene: Scene;
  private readonly material: Material;
  private readonly chunks = new Map<string, Chunk>();
  private readonly vectorChunks = new Map<string, VectorChunk>();
  private origin: GeoOrigin | undefined;
  /**
   * Walkable lanes recovered from each chunk's roads, for the villagers.
   * Lives here because this is the one place that knows when a chunk's
   * geometry — and so its roads — arrives and leaves.
   */
  readonly footpaths = new FootpathNetwork();
  /** Traffic lanes recovered from each chunk's bigger roads, for the cars. */
  readonly lanes = new LaneNetwork();
  /** The tram tracks streamed in, for the trams to run on: see `tracks.ts`. */
  readonly tracks = new TrackNetwork();
  /** The railways, for the trains: see `trains.ts`. */
  readonly railways = new TrackNetwork(ROAD_STYLE.rail);
  /** Level crossings, found where roads cross the railways: see `railCrossings.ts`. */
  readonly crossings = new RailCrossings();
  /**
   * Landcover rasters by tile id. They arrive with vector chunks but are drawn
   * by the terrain chunk of the same tile, and either may arrive first, so the
   * two are joined here whenever both exist.
   */
  private readonly landcover = new Map<string, DataTexture>();
  /** Built surfaces to stand on — roads, bridges, piers, pitches — per chunk. */
  private readonly walkable = new Map<string, Walkable>();
  /**
   * New tiles' meshes waiting to be shown, a few megabytes a frame: each one
   * shown is uploaded to the GPU in the frame it is first drawn, and a whole
   * tile at once — buildings, trees, roads — is a freeze on a phone.
   */
  private readonly reveal: Array<{ alive: () => boolean; hidden: Object3D[]; done?: () => void }> = [];
  /** Terrain pieces whose heights changed, queued so their 9 MB buffer re-upload spreads across frames. */
  private readonly dirtyPieces: Mesh[] = [];
  /** Materials whose shader programs have already been compiled through `prepare`. */
  private readonly compiledMaterials = new WeakSet<Material>();
  /** Buildings, trunks and posts per chunk, for pushing bodies out of them. */
  private readonly solid = new Map<string, { group: Group; index: CollisionIndex }>();

  /**
   * Earlier versions of a tile, kept on screen until the new one is ready to
   * show, so a tile being rebuilt never blinks out.
   */
  private readonly retiring = new Map<string, VectorChunk[]>();

  /**
   * Parked vehicles that have been driven away, by chunk, in chunk metres. A
   * rebuilt tile would park them again otherwise.
   */
  private readonly taken = new Map<string, Array<{ x: number; z: number; species?: undefined } | PropChange>>();

  constructor(
    scene: Scene,
    material: Material,
    /**
     * Compile an object's shaders without stalling a frame — resolves when it
     * can be drawn. A tile is attached to the scene only once this is done:
     * a shader compiled mid-frame the first time a material is drawn is a
     * freeze, tens to hundreds of milliseconds on a phone's GPU.
     */
    private readonly prepare?: (object: Object3D, scene: Scene) => Promise<unknown>,
    private readonly options: RendererOptions = {},
  ) {
    this.scene = scene;
    this.material = material;
  }

  /**
   * Scene position to latitude and longitude, from the world: a parked car's
   * fuel is found from where it stands, as it is when it is taken.
   */
  toGeo: ((east: number, north: number) => { lat: number; lon: number }) | undefined;

  get chunkCount(): number {
    return this.chunks.size;
  }

  get treeCount(): number {
    let n = 0;
    for (const chunk of this.vectorChunks.values()) {
      for (const mesh of chunk.parts.flora) n += mesh.count;
    }
    return n;
  }

  setOrigin(origin: GeoOrigin): void {
    this.origin = origin;
    // Re-derive every chunk's position from its own tile key. Translating them
    // all by a fixed offset would be wrong: the two frames scale their east
    // axis by the cosine of different latitudes. See `rebaseOffset`.
    for (const chunk of this.chunks.values()) {
      this.place(chunk.mesh, chunk.key);
    }
    for (const chunk of this.vectorChunks.values()) {
      this.place(chunk.group, chunk.key);
    }
    for (const list of this.retiring.values()) for (const chunk of list) this.place(chunk.group, chunk.key);
    this.refreshLampMap();
  }

  addTerrainChunk(data: TerrainMeshData): void {
    const id = tileKeyToString(data.key);
    // A tile can legitimately arrive twice — evicted from the cache and
    // refetched — so replace rather than stacking two meshes in the same place.
    this.removeTerrainChunk(data.key);

    // Only hilly/mountainous terrain can cast a projected ridge shadow across
    // a valley; flat and rolling tiles are self-shaded by their normals, and
    // skipping them saves hundreds of thousands of shadow-map triangles a frame.
    const casts = data.key.z >= 13 && data.maxHeight - data.minHeight > 35;

    // The near terrain's flat facets, made in the worker, as pieces the frame's
    // upload budget can take one at a time: a whole tile is 20 MB in one go.
    const made = data.facets;
    if (made !== undefined && data.key.z >= 14 && this.options.facetTerrain !== false) {
      // The geometry owns them now; the tile's data in the cache stays as small as before.
      data.facets = undefined;
      const group = new Group();
      group.name = id;
      group.matrixAutoUpdate = false;
      const pieces: Mesh[] = [];
      const faces = made.positions.length / 9;
      for (let p = 0; p < TERRAIN_PIECES; p++) {
        const a = Math.floor((faces * p) / TERRAIN_PIECES) * 3, b = Math.floor((faces * (p + 1)) / TERRAIN_PIECES) * 3;
        if (b <= a) continue;
        const g = new BufferGeometry();
        g.setAttribute('position', new BufferAttribute(made.positions.subarray(a * 3, b * 3), 3));
        g.setAttribute('normal', new BufferAttribute(made.normals.subarray(a * 3, b * 3), 3));
        g.setAttribute('uv', new BufferAttribute(made.uvs.subarray(a * 2, b * 2), 2));
        g.setAttribute('aGround', new BufferAttribute(made.groundColors.subarray(a * 4, b * 4), 4, true));
        g.setAttribute('aPatch', new BufferAttribute(made.patchColors.subarray(a * 4, b * 4), 4, true));
        g.setAttribute('aRock', new BufferAttribute(made.rockColors.subarray(a * 4, b * 4), 4, true));
        g.setAttribute('color', new BufferAttribute(made.shades.subarray(a * 3, b * 3), 3, true));
        g.computeBoundingSphere();
        const piece = new Mesh(g, this.material);
        piece.layers.enable(GROUND_LAYER);
        piece.receiveShadow = true;
        piece.castShadow = casts;
        piece.matrixAutoUpdate = false;
        pieces.push(piece);
      }
      group.add(...pieces);
      this.place(group, data.key);
      this.scene.add(group);
      this.chunks.set(id, { mesh: group, pieces, key: data.key, width: data.width, depth: data.depth, expandedFrom: data.indices, facets: { positions: made.positions, normals: made.normals } });
      if (this.options.uploadBudget !== undefined) {
        for (const piece of pieces) piece.visible = false;
        this.reveal.unshift({ alive: () => this.chunks.get(id)?.mesh === group, hidden: [...pieces] });
      }
      this.linkLandcover(id);
      return;
    }

    let geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(data.positions, 3));
    geometry.setAttribute('normal', new BufferAttribute(data.normals, 3));
    geometry.setAttribute('uv', new BufferAttribute(data.uvs, 2));
    // The biome palette, baked per vertex in the worker. See terrainMaterial.
    geometry.setAttribute('aGround', new BufferAttribute(data.groundColors, 4, true));
    geometry.setAttribute('aPatch', new BufferAttribute(data.patchColors, 4, true));
    geometry.setAttribute('aRock', new BufferAttribute(data.rockColors, 4, true));
    geometry.setIndex(new BufferAttribute(data.indices, 1));
    // Individual face colors make the low-poly surface legible even on flat land.
    // Only the near terrain needs duplicated vertices; distant chunks stay compact.
    const facets = data.key.z >= 14 && this.options.facetTerrain !== false;
    const expandedFrom = facets ? data.indices : undefined;
    if (facets) {
      const indexed = geometry;
      geometry = indexed.toNonIndexed();
      indexed.dispose();
      const colors = new Float32Array(geometry.getAttribute('position').count * 3);
      for (let face = 0; face < colors.length / 9; face++) {
        const hash = Math.imul(face + data.key.x * 131 + data.key.y * 719, 2654435761) >>> 0;
        // A light touch: with shadows and vivid grass, a heavy facet jitter reads
        // as noise rather than as a low-poly surface.
        const shade = 0.93 + (hash / 4294967296) * 0.09;
        for (let v = 0; v < 3; v++) {
          const offset = face * 9 + v * 3;
          colors[offset] = shade;
          colors[offset + 1] = shade;
          colors[offset + 2] = shade;
        }
      }
      geometry.setAttribute('color', new BufferAttribute(colors, 3));
    } else {
      const colors = new Float32Array(data.positions.length).fill(1);
      geometry.setAttribute('color', new BufferAttribute(colors, 3));
    }
    geometry.computeBoundingSphere();

    const mesh = new Mesh(geometry, this.material);
    mesh.name = id;
    // The snow drapes itself over the ground it sees from above (see snowField.ts).
    mesh.layers.enable(GROUND_LAYER);
    // Terrain both catches shadows and throws them: a ridge shading the valley
    // beside it is as much of the relief as the geometry is.
    mesh.receiveShadow = true;
    mesh.castShadow = casts;
    mesh.matrixAutoUpdate = false;
    this.place(mesh, data.key);
    this.scene.add(mesh);
    this.chunks.set(id, { mesh, pieces: [mesh], key: data.key, width: data.width, depth: data.depth, expandedFrom });
    // Shown within the frame's upload budget like a tile's meshes — ahead of
    // them: the ground first.
    if (this.options.uploadBudget !== undefined) {
      mesh.visible = false;
      this.reveal.unshift({ alive: () => this.chunks.get(id)?.mesh === mesh, hidden: [mesh] });
    }
    this.linkLandcover(id);
  }

  addVectorChunk(data: PlacedVectorChunk): void {
    const id = tileKeyToString(data.key);
    // The version on screen stays there until this one is ready.
    const previous = this.vectorChunks.get(id);
    if (previous !== undefined) {
      this.vectorChunks.delete(id);
      this.footpaths.remove(id);
      this.lanes.remove(id);
      this.tracks.remove(id);
      this.railways.remove(id);
      this.crossings.remove(id);
      this.walkable.delete(id);
      this.solid.delete(id);
      this.dropLandcover(id);
      this.retiring.set(id, [...(this.retiring.get(id) ?? []), previous]);
    }

    const parts = buildVegetation(data);
    // Where the paths cross the traffic's lanes, and meet each other (see
    // core/character/footpaths.ts), and the zebras painted there (core/build/zebras.ts).
    // Made by the placing worker (`placeVectorChunk`); here only where a tile was placed without one.
    let footpaths = data.footpaths, lanes = data.lanes, zebras = data.zebras;
    if (footpaths === undefined || lanes === undefined || zebras === undefined) {
      footpaths = extractFootpaths(data.roadPositions, data.roadIndices, {}, data.roadAttributes);
      lanes = extractLanes(data.roadPositions, data.roadIndices, data.roadAttributes);
      annotateFootpaths(footpaths, lanes);
      zebras = zebraMarks(data.roadPositions, data.roadIndices, data.roadAttributes, crossingPoints(footpaths));
    }
    parts.roads?.geometry.setAttribute('aZebra', new BufferAttribute(zebras, 2));
    const group = new Group();
    if (parts.flora.length > 0) group.add(...parts.flora);
    this.place(group, data.key);
    parts.exhausts = this.buildExhausts(parts, group);
    if (parts.exhausts !== undefined) group.add(parts.exhausts);
    group.matrixAutoUpdate = false;
    if (parts.water !== undefined) group.add(parts.water);
    if (parts.roads !== undefined) group.add(parts.roads);
    for (const extra of [parts.bridges, parts.tunnels, parts.waterways, parts.piers, parts.surfaces, parts.pools]) {
      if (extra !== undefined) group.add(extra);
    }
    if (parts.buildings.length > 0) group.add(...parts.buildings);
    if (parts.signs !== undefined) group.add(parts.signs);
    if (parts.helipads !== undefined) group.add(parts.helipads);
    if (group.children.length === 0) {
      this.flushRetiring(id);
      return;
    }
    for (const child of group.children) {
      child.receiveShadow = true;
      // Flat surfaces and tiny 20cm grass tufts never shade anything, so they
      // do not go into the shadow map. Everything that stands up does.
      child.castShadow = child !== parts.water && child !== parts.roads && child !== parts.tunnels &&
        child !== parts.waterways && child !== parts.surfaces && child !== parts.pools && child !== parts.signs &&
        child !== parts.helipads && child.name !== 'flora:tuft' && child.name !== 'flora:dryTuft';
      if (child === parts.pools) child.receiveShadow = false;
    }
    if (this.options.propShadows === false) {
      // Only what stands taller than a person casts: trees, not lamps, fences, bins or cars.
      for (const mesh of parts.flora) {
        const g = mesh.geometry;
        if (g.boundingBox === null) g.computeBoundingBox();
        mesh.castShadow = g.boundingBox !== null && g.boundingBox.max.y - g.boundingBox.min.y > 3;
      }
      if (parts.exhausts !== undefined) parts.exhausts.castShadow = false;
    }

    this.vectorChunks.set(id, { group, parts, key: data.key, helipads: data.helipads ?? [], energy: data.energy ?? [], stations: data.stations ?? [], railStations: data.railStations ?? [], garages: data.garages ?? [] });
    const show = () => {
      // Replaced or removed while its shaders compiled: nothing to show.
      if (this.vectorChunks.get(id)?.group !== group) return;
      if (this.options.uploadBudget === undefined) {
        this.scene.add(group);
        this.flushRetiring(id);
        return;
      }
      // Shown a few meshes a frame by `pump`; the old version stays until it is whole.
      const hidden = [...group.children];
      for (const child of hidden) child.visible = false;
      this.scene.add(group);
      this.reveal.push({ alive: () => this.vectorChunks.get(id)?.group === group, hidden, done: () => this.flushRetiring(id) });
    };
    let needsCompile = false;
    if (this.prepare !== undefined) {
      for (const child of group.children) {
        const mat = (child as Mesh).material as Material | undefined;
        if (mat !== undefined && !this.compiledMaterials.has(mat)) {
          needsCompile = true;
          break;
        }
      }
    }
    if (!needsCompile || this.prepare === undefined) {
      show();
    } else {
      const done = () => {
        for (const child of group.children) {
          const mat = (child as Mesh).material as Material | undefined;
          if (mat !== undefined && child !== parts.signs) this.compiledMaterials.add(mat);
        }
        show();
      };
      this.prepare(group, this.scene).then(done, done);
    }
    this.footpaths.add(id, group, footpaths);
    this.lanes.add(id, group, lanes);
    this.tracks.add(id, group, data.roadPositions, data.roadIndices, data.roadAttributes);
    this.railways.add(id, group, data.roadPositions, data.roadIndices, data.roadAttributes);
    this.crossings.add(id, group, data.roadPositions, data.roadIndices, data.roadAttributes);
    this.solid.set(id, { group, index: new CollisionIndex(data.colliders) });
    for (const spot of this.taken.get(id) ?? []) {
      if (spot.species === undefined) this.hideParked(id, parts, spot.x, spot.z);
      else this.applyProp(id, parts, spot);
    }
    const extent = tileExtent(data.key, this.origin!);
    this.walkable.set(id, {
      group,
      width: extent.maxEast - extent.minEast,
      depth: extent.maxNorth - extent.minNorth,
      // Indexed later, in a quiet frame, or at once when first stood on.
      meshes: [
        { positions: data.roadPositions, indices: data.roadIndices },
        { positions: data.pierPositions, indices: data.pierIndices },
        { positions: data.surfacePositions, indices: data.surfaceIndices },
        // Road banks: from their foot, just under the grass, up to the kerb —
        // climbed a step at a time, they lead onto the road they carry.
        { positions: data.embankmentPositions, indices: data.embankmentIndices },
        // Stairs: mapped steps, and the towers up to rooftop pads.
        { positions: data.stairPositions ?? new Float32Array(0), indices: data.stairIndices ?? new Uint32Array(0) },
        // Roofs, and the pads on them.
        { positions: data.buildingPositions, indices: data.roofIndices ?? new Uint32Array(0) },
        helipadDiscs(data.helipads ?? []),
        // Tunnel floors, walkways and all, and the troughs' floors.
        { positions: data.tunnelFloorPositions ?? new Float32Array(0), indices: data.tunnelFloorIndices ?? new Uint32Array(0) },
        { positions: data.troughFloorPositions ?? new Float32Array(0), indices: data.troughFloorIndices ?? new Uint32Array(0) },
        // The quays' aprons by a canal: the street's level up to the wall.
        { positions: data.quayFloorPositions ?? new Float32Array(0), indices: data.quayFloorIndices ?? new Uint32Array(0) },
      ],
      tunnel: (data.tunnelFloorIndices?.length ?? 0) > 0 ? { positions: data.tunnelFloorPositions!, indices: data.tunnelFloorIndices! } : undefined,
      roof: (data.tunnelFloorIndices?.length ?? 0) > 0 && data.tunnelRoofPositions !== undefined ? { positions: data.tunnelRoofPositions, indices: data.tunnelFloorIndices! } : undefined,
      rooftops: [
        { positions: data.buildingPositions, indices: data.roofIndices ?? new Uint32Array(0) },
        helipadDiscs(data.helipads ?? []),
      ],
      portals: data.portals ?? [],
    });
    if (data.landcoverSize > 0) {
      const texture = new DataTexture(data.landcover, data.landcoverSize, data.landcoverSize, RedFormat, UnsignedByteType);
      texture.magFilter = NearestFilter;
      texture.minFilter = NearestFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      this.landcover.set(id, texture);
      this.linkLandcover(id);
    }
    this.refreshLampMap();
  }

  removeVectorChunk(key: TileKey): void {
    const id = tileKeyToString(key);
    this.flushRetiring(id);
    const chunk = this.vectorChunks.get(id);
    if (chunk === undefined) return;
    this.scene.remove(chunk.group);
    disposeVegetation(chunk.parts);
    this.vectorChunks.delete(id);
    this.footpaths.remove(id);
    this.lanes.remove(id);
    this.tracks.remove(id);
    this.railways.remove(id);
    this.crossings.remove(id);
    this.walkable.delete(id);
    this.solid.delete(id);
    this.dropLandcover(id);
    this.refreshLampMap();
  }

  private flushRetiring(id: string): void {
    for (const old of this.retiring.get(id) ?? []) {
      this.scene.remove(old.group);
      disposeVegetation(old.parts);
    }
    this.retiring.delete(id);
  }

  /**
   * The roads reshaped a chunk: copy its new positions and normals into the
   * geometry already on the GPU — through its index, where the geometry was
   * expanded to flat facets — instead of building it again. A few hundred
   * thousand floats copied rather than a mesh rebuilt and re-uploaded whole.
   */
  updateTerrainHeights(data: TerrainMeshData): void {
    const chunk = this.chunks.get(tileKeyToString(data.key));
    if (chunk === undefined) {
      this.addTerrainChunk(data);
      return;
    }
    const first = chunk.pieces[0].geometry;
    const index = chunk.expandedFrom;
    if (index === undefined) {
      (first.getAttribute('position').array as Float32Array).set(data.positions);
      (first.getAttribute('normal').array as Float32Array).set(data.normals);
    } else {
      // Into the whole arrays, which the pieces are views of.
      const p = chunk.facets?.positions ?? first.getAttribute('position').array as Float32Array;
      const n = chunk.facets?.normals ?? first.getAttribute('normal').array as Float32Array;
      const pos = data.positions, norm = data.normals, len = index.length;
      for (let i = 0; i < len; i++) {
        const v = index[i] * 3, i3 = i * 3;
        p[i3 + 1] = pos[v + 1];
        n[i3] = norm[v]; n[i3 + 1] = norm[v + 1]; n[i3 + 2] = norm[v + 2];
      }
    }
    if (this.options.uploadBudget !== undefined && chunk.pieces.length > 1) {
      chunk.pieces[0].geometry.getAttribute('position').needsUpdate = true;
      chunk.pieces[0].geometry.getAttribute('normal').needsUpdate = true;
      for (let i = 1; i < chunk.pieces.length; i++) this.dirtyPieces.push(chunk.pieces[i]);
    } else {
      for (const piece of chunk.pieces) {
        piece.geometry.getAttribute('position').needsUpdate = true;
        piece.geometry.getAttribute('normal').needsUpdate = true;
        if (piece.geometry.boundingSphere === null) piece.geometry.computeBoundingSphere();
      }
    }
  }

  removeTerrainChunk(key: TileKey): void {
    const id = tileKeyToString(key);
    const chunk = this.chunks.get(id);
    if (chunk === undefined) return;
    this.scene.remove(chunk.mesh);
    for (const piece of chunk.pieces) piece.geometry.dispose();
    chunk.own?.dispose();
    this.chunks.delete(id);
  }

  /**
   * The highest built surface at a scene position that is no higher than
   * `ceiling` (scene metres), or undefined where there is none — the terrain
   * is the ground there.
   */
  surfaceHeightAt(x: number, z: number, ceiling: number): number | undefined {
    let best: number | undefined;
    for (const w of this.walkable.values()) {
      const lx = x - w.group.position.x, lz = z - w.group.position.z;
      if (lx < -40 || lz < -40 || lx > w.width + 40 || lz > w.depth + 40) continue;
      w.index ??= new SurfaceIndex(w.meshes);
      const y = w.index.heightAt(lx, lz, ceiling);
      if (y !== undefined && (best === undefined || y > best)) best = y;
    }
    return best;
  }

  /**
   * The highest roof or rooftop pad at a scene position no higher than
   * `ceiling`, or undefined. Unlike `surfaceHeightAt`, never a road, a bridge
   * deck or a pier: up on a roof the building is underfoot, but on a bridge
   * its guardrails are still in the way.
   */
  rooftopHeightAt(x: number, z: number, ceiling: number): number | undefined {
    let best: number | undefined;
    for (const w of this.walkable.values()) {
      const lx = x - w.group.position.x, lz = z - w.group.position.z;
      if (lx < -40 || lz < -40 || lx > w.width + 40 || lz > w.depth + 40) continue;
      w.rooftopIndex ??= new SurfaceIndex(w.rooftops);
      const y = w.rooftopIndex.heightAt(lx, lz, ceiling);
      if (y !== undefined && (best === undefined || y > best)) best = y;
    }
    return best;
  }

  /**
   * Whether scene (x, z) at height `y` is inside a tunnel, on its floor, or
   * in a portal's mouth: where the terrain over it is not the ground.
   */
  inTunnel(x: number, z: number, y: number): boolean {
    for (const w of this.walkable.values()) {
      if (w.tunnel === undefined && w.portals.length === 0) continue;
      const lx = x - w.group.position.x, lz = z - w.group.position.z;
      if (lx < -40 || lz < -40 || lx > w.width + 40 || lz > w.depth + 40) continue;
      if (w.portals.some(p => inMouth(p, lx, y, lz))) return true;
      if (w.tunnel === undefined) continue;
      w.tunnelIndex ??= new SurfaceIndex([w.tunnel]);
      const floor = w.tunnelIndex.heightAt(lx, lz, y + 0.5);
      if (floor !== undefined && y - floor < 1.5) return true;
      if (floor !== undefined && w.roof !== undefined) {
        w.roofIndex ??= new SurfaceIndex([w.roof]);
        const roof = w.roofIndex.heightAt(lx, lz, floor + 9);
        if (roof !== undefined && y < roof - 0.05) return true;
      }
    }
    return false;
  }

  private readonly portalScratch: Array<{ d: number; p: Portal; ox: number; oy: number; oz: number }> = [];

  /** Hand the terrain the portals nearest scene (x, z), whose mouths it leaves open. Once a frame. */
  updatePortals(x: number, z: number): void {
    const near = this.portalScratch;
    let count = 0;
    for (const w of this.walkable.values()) {
      const portals = w.portals;
      if (portals.length === 0) continue;
      const ox = w.group.position.x, oy = w.group.position.y, oz = w.group.position.z;
      for (let i = 0; i < portals.length; i++) {
        const p = portals[i];
        const d = Math.hypot(p.x + ox - x, p.z + oz - z);
        if (count < near.length) {
          const slot = near[count];
          slot.d = d; slot.p = p; slot.ox = ox; slot.oy = oy; slot.oz = oz;
        } else {
          near.push({ d, p, ox, oy, oz });
        }
        count++;
      }
    }
    near.length = count;
    if (count > 1) near.sort((a, b) => a.d - b.d);
    const n = Math.min(PORTAL_SLOTS, count);
    for (let i = 0; i < n; i++) {
      const { p, ox, oy, oz } = near[i];
      terrainPortals.a.value[i].set(p.x + ox, p.z + oz, p.dx, p.dz);
      terrainPortals.b.value[i].set(p.halfWidth, p.length, p.floor + oy, p.ceiling + oy);
      terrainPortals.c.value[i].set(p.tubeHalfWidth ?? p.halfWidth, 0, 0, 0);
    }
    terrainPortals.count.value = n;
  }

  /** The ceiling over scene (x, z) for a body at height `y` inside a tunnel, or undefined in the open. */
  tunnelCeiling(x: number, z: number, y: number): number | undefined {
    if (!this.inTunnel(x, z, y)) return undefined;
    for (const w of this.walkable.values()) {
      if (w.roof === undefined) continue;
      const lx = x - w.group.position.x, lz = z - w.group.position.z;
      if (lx < -40 || lz < -40 || lx > w.width + 40 || lz > w.depth + 40) continue;
      w.roofIndex ??= new SurfaceIndex([w.roof]);
      const roof = w.roofIndex.heightAt(lx, lz, y + 9);
      if (roof !== undefined && roof > y) return roof;
    }
    return undefined;
  }

  /**
   * The ground under scene (x, z), for something whose feet are within a
   * step under `ceiling`: the highest built surface there, if it is over the
   * terrain, or if it is a tunnel's floor near the feet, under the hill
   * over it. Else the terrain.
   */
  ground(x: number, z: number, terrain: number, ceiling: number): number {
    const surface = this.surfaceHeightAt(x, z, ceiling);
    // Keep a body already in a tube on that floor. The general step query
    // can otherwise select an overlapping street or another tunnel up to
    // 75 cm above it, snapping the body onto the wrong vertical surface.
    let tunnelFloor: number | undefined;
    for (const w of this.walkable.values()) {
      if (w.tunnel === undefined) continue;
      const lx = x - w.group.position.x, lz = z - w.group.position.z;
      if (lx < -40 || lz < -40 || lx > w.width + 40 || lz > w.depth + 40) continue;
      w.tunnelIndex ??= new SurfaceIndex([w.tunnel]);
      const floor = w.tunnelIndex.heightAt(lx, lz, ceiling - 0.4) ?? w.tunnelIndex.heightAt(lx, lz, ceiling);
      if (floor === undefined) continue;
      let belowRoof = false;
      if (w.roof !== undefined) {
        w.roofIndex ??= new SurfaceIndex([w.roof]);
        const roof = w.roofIndex.heightAt(lx, lz, floor + 9);
        belowRoof = roof !== undefined && ceiling - 0.75 < roof - 0.05;
      }
      // A car can be airborne inside a tube, or its front axle can be
      // farther up the ramp than its centre. Neither puts it on the hill
      // above. Use the full step allowance and retain the enclosing roof.
      if ((floor > ceiling - TUNNEL_REACH || belowRoof) && (tunnelFloor === undefined || floor > tunnelFloor)) tunnelFloor = floor;
    }
    if (tunnelFloor !== undefined) {
      // The road ribbon lies a few centimetres over the tube's walkway.
      return Math.max(tunnelFloor, this.surfaceHeightAt(x, z, tunnelFloor + 0.2) ?? tunnelFloor);
    }
    if (surface === undefined) return terrain;
    if (surface > terrain) return surface;
    return surface > ceiling - TUNNEL_REACH && this.inTunnel(x, z, surface) ? surface : terrain;
  }

  /** A new `uploadBudget`, from the next frame: only its size, not whether tiles are shown piece by piece. */
  setUploadBudget(bytes: number): void {
    if (this.options.uploadBudget !== undefined) this.options.uploadBudget = bytes;
  }

  /**
   * A frame's share of the work a new tile brings: its meshes shown up to
   * `uploadBudget` bytes of GPU buffers, then — once nothing is waiting to
   * be shown — one tile's walking surfaces indexed. Called once a frame.
   */
  pump(): void {
    let budget = this.options.uploadBudget ?? Infinity;
    while (this.dirtyPieces.length > 0 && budget > 0) {
      const piece = this.dirtyPieces.shift()!;
      const pos = piece.geometry.getAttribute('position') as BufferAttribute | undefined;
      const norm = piece.geometry.getAttribute('normal') as BufferAttribute | undefined;
      if (pos !== undefined) {
        pos.needsUpdate = true;
        budget -= (pos.array as { byteLength: number }).byteLength;
      }
      if (norm !== undefined) {
        norm.needsUpdate = true;
        budget -= (norm.array as { byteLength: number }).byteLength;
      }
    }
    while (this.reveal.length > 0 && budget > 0) {
      const job = this.reveal[0];
      if (!job.alive()) { this.reveal.shift(); continue; }
      const child = job.hidden.shift();
      if (child !== undefined) {
        child.visible = true;
        budget -= gpuBytes(child);
      }
      if (job.hidden.length === 0) {
        this.reveal.shift();
        job.done?.();
      }
    }
    if (this.reveal.length > 0 || this.dirtyPieces.length > 0) return;
    for (const w of this.walkable.values()) {
      if (w.index !== undefined) continue;
      w.index = new SurfaceIndex(w.meshes);
      break;
    }
  }

  /**
   * Push a round body at scene (x, z), spanning heights `feet`..`head`, out of
   * any building, trunk or post it overlaps, in whichever tile holds them.
   * Returns where it may stand and the push normal, or undefined when clear.
   */
  collide(x: number, z: number, radius: number, feet: number, head: number, skip?: (species: number) => boolean):
    { x: number; z: number; nx: number; nz: number; width: number; species: number; cx: number; cz: number } | undefined {
    let px = x, pz = z, hit = false, width = Infinity, species = -1, cx = 0, cz = 0;
    // A mapped tube through a building is a real opening. Its own walls
    // remain solid; only the enclosing building's footprint gives way.
    const ceiling = this.tunnelCeiling(x, z, feet);
    const passage = ceiling !== undefined ? head <= ceiling : this.inTunnel(x, z, feet);
    for (const { group, index } of this.solid.values()) {
      const ox = group.position.x, oz = group.position.z;
      const out = index.resolve(px - ox, pz - oz, radius, feet, head, skip, passage);
      if (out === undefined) continue;
      px = out.x + ox; pz = out.z + oz; hit = true;
      if (out.width < width || (!Number.isFinite(width) && species === -1)) {
        width = Math.min(width, out.width);
        species = out.species; cx = out.cx + ox; cz = out.cz + oz;
      }
    }
    if (!hit) return undefined;
    const dx = px - x, dz = pz - z, len = Math.hypot(dx, dz) || 1;
    return { x: px, z: pz, nx: dx / len, nz: dz / len, width, species, cx, cz };
  }

  /**
   * The props of `species` standing within `radius` of scene (x, z), nearest
   * first: each one's place and how it stands. For knocking one over, or
   * flattening a bush.
   */
  propsNear(x: number, z: number, radius: number, species: ReadonlySet<Species>): PropRef[] {
    const out: PropRef[] = [];
    for (const [chunk, { group, parts }] of this.vectorChunks) {
      const lx = x - group.position.x, lz = z - group.position.z;
      for (const mesh of parts.flora) {
        const name = mesh.name.slice('flora:'.length) as Species;
        if (!species.has(name)) continue;
        // A whole tile's bushes, nowhere near: not looked through one by one.
        const sphere = mesh.boundingSphere;
        if (sphere !== null && Math.hypot(sphere.center.x - lx, sphere.center.z - lz) > sphere.radius + radius) continue;
        const e = mesh.instanceMatrix.array;
        for (let i = 0; i < mesh.count; i++) {
          const o = i * 16;
          if (e[o] === 0 && e[o + 2] === 0) continue;
          const d = Math.hypot(e[o + 12] - lx, e[o + 14] - lz);
          if (d > radius) continue;
          out.push({ chunk, mesh, index: i, species: name, x: e[o + 12] + group.position.x, y: e[o + 13], z: e[o + 14] + group.position.z, d });
        }
      }
    }
    return out.sort((a, b) => a.d - b.d);
  }

  /**
   * Take a prop out of the world, for good: it stops being drawn and being
   * solid, all its colliders within `reach` of where it stands. Its pieces
   * are drawn elsewhere.
   */
  takeProp(ref: PropRef, reach = 0.5): void {
    this.alterProp(ref, 'gone', undefined, 0, reach);
  }

  /**
   * Flatten a prop where it stands: a bush or a hedge driven over, crushed
   * down to a third of its height and laid over the way it was pushed
   * (scene x, z). It no longer stops anyone.
   */
  flattenProp(ref: PropRef, push: { x: number; z: number }): void {
    this.alterProp(ref, 'flat', push);
  }

  /**
   * Bend a prop over where it stands, by `angle` radians toward the way it
   * was pushed: a lamp post leaning after a knock, a bin knocked crooked.
   * Still solid.
   */
  bendProp(ref: PropRef, push: { x: number; z: number }, angle: number): void {
    this.alterProp(ref, 'bent', push, angle);
  }

  /**
   * Take the bus stop's name board off the shelter standing as `ref` (see
   * `busStopSign`): it is drawn in the tile's signs, hung from the front of
   * the shelter's roof, and would hang there in the air with the shelter
   * gone. Its faces are folded away in the signs; returned are its corners
   * (scene metres, the face's own order: lower left, lower right, upper
   * right, upper left, looking at it), the patch of the atlas it shows, and
   * the signs' material, to draw it as a piece of its own.
   */
  takeStopBoard(ref: PropRef): { corners: Vector3[]; uvs: number[]; material: Material } | undefined {
    const chunk = this.vectorChunks.get(ref.chunk);
    if (chunk === undefined) return undefined;
    const x = ref.x - chunk.group.position.x, z = ref.z - chunk.group.position.z;
    const faces = hideStopBoard(chunk.parts.signs, x, ref.y, z);
    if (faces.length === 0) return undefined;
    const o = chunk.group.position;
    const first = faces[0];
    return {
      corners: first.corners.map(c => c.add(o)),
      uvs: first.uvs,
      material: chunk.parts.signs!.material as Material,
    };
  }

  private alterProp(ref: PropRef, how: 'gone' | 'flat' | 'bent', push = { x: 0, z: 0 }, angle = 0, reach = 0.5): void {
    const chunk = this.vectorChunks.get(ref.chunk);
    if (chunk === undefined) return;
    const x = ref.x - chunk.group.position.x, z = ref.z - chunk.group.position.z;
    let list = this.taken.get(ref.chunk);
    if (list === undefined) this.taken.set(ref.chunk, (list = []));
    const species = SPECIES_ID[ref.species];
    // Only the latest of what happened to it matters, on a rebuild.
    const was = list.findIndex(t => t.species === species && Math.hypot(t.x - x, t.z - z) < 0.3);
    if (was >= 0) list.splice(was, 1);
    list.push({ x, z, species, how, push, angle, reach });
    this.applyProp(ref.chunk, chunk.parts, { x, z, species, how, push, angle, reach });
  }

  /** Do to a tile's prop what was done to it: on the spot, and again when the tile is rebuilt. */
  private applyProp(id: string, parts: VegetationChunk, t: PropChange): void {
    const name = SPECIES[t.species];
    const mesh = parts.flora.find(m => m.name === `flora:${name}`);
    if (mesh === undefined) return;
    for (let i = 0; i < mesh.count; i++) {
      mesh.getMatrixAt(i, scratchMatrix);
      const e = scratchMatrix.elements;
      if (Math.hypot(e[12] - t.x, e[14] - t.z) > 0.3) continue;
      if (t.how === 'gone') {
        mesh.setMatrixAt(i, ZERO);
      } else {
        // Laid over about its foot, toward the push: a turn about the
        // horizontal axis across it, (push × up).
        const len = Math.hypot(t.push.x, t.push.z) || 1;
        bendAxis.set(t.push.z / len, 0, -t.push.x / len);
        const tilt = t.how === 'flat' ? 1.1 : t.angle;
        bendTurn.makeRotationAxis(bendAxis, tilt);
        if (t.how === 'flat') bendTurn.multiply(bendSquash.makeScale(1, 0.35, 1));
        // About its foot, where it stands: kept before the turn is put on.
        const fx = e[12], fy = e[13], fz = e[14];
        scratchMatrix.setPosition(0, 0, 0);
        scratchMatrix.premultiply(bendTurn);
        scratchMatrix.setPosition(fx, fy, fz);
        mesh.setMatrixAt(i, scratchMatrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      // A shelter gone takes its stop's name board with it.
      if (t.how === 'gone' && SPECIES[t.species] === 'shelter') hideStopBoard(parts.signs, t.x, e[13], t.z);
      if (name === 'lamp' && parts.pools !== undefined) {
        const roots = parts.pools.userData.roots as Float32Array | undefined;
        const lamps = parts.pools.userData.lamps as Float32Array | undefined;
        if (roots !== undefined && i * 2 + 1 < roots.length && Math.hypot(roots[i * 2] - t.x, roots[i * 2 + 1] - t.z) <= 0.35) {
          parts.pools.setMatrixAt(i, ZERO);
          parts.pools.instanceMatrix.needsUpdate = true;
          if (t.how === 'gone' && lamps !== undefined) {
            lamps[i * 2] = NaN;
            lamps[i * 2 + 1] = NaN;
            this.refreshLampMap();
          }
        }
      }
      break;
    }
    if (t.how !== 'bent') this.solid.get(id)?.index.disableNear(t.x, t.z, t.reach, t.species);
  }

  /**
   * The nearest place selling energy from `sources` that scene (x, z) is
   * within reach of, if any: a forecourt, a charging point, a driveway.
   */
  energyAt(x: number, z: number, sources: readonly Source[]): EnergySpot | undefined {
    let best: EnergySpot | undefined;
    let bestD = Infinity;
    for (const { group, energy } of this.vectorChunks.values()) {
      const lx = x - group.position.x, lz = z - group.position.z;
      for (const e of energy) {
        if (!sources.includes(e.source)) continue;
        const d = Math.hypot(e.x - lx, e.z - lz);
        if (d <= e.radius && d < bestD) { bestD = d; best = e; }
      }
    }
    return best;
  }

  /** The police stations in the tiles now loaded within `radius` of scene (x, z): each one's place, in scene coordinates, and an id. */
  policeStations(x: number, z: number, radius: number): Array<{ id: string; x: number; z: number; name: string }> {
    const out: Array<{ id: string; x: number; z: number; name: string }> = [];
    for (const [chunk, { group, stations }] of this.vectorChunks) {
      stations.forEach((s, i) => {
        const sx = s.x + group.position.x, sz = s.z + group.position.z;
        if (Math.hypot(sx - x, sz - z) <= radius) out.push({ id: `${chunk}#${i}`, x: sx, z: sz, name: s.name });
      });
    }
    return out;
  }

  /** The railway stations in the tiles now loaded within `radius` of scene (x, z): each one's place, in scene coordinates, and its name. */
  railStationsNear(x: number, z: number, radius: number): RailStation[] {
    const out: RailStation[] = [];
    for (const { group, railStations } of this.vectorChunks.values()) {
      for (const s of railStations) {
        const sx = s.x + group.position.x, sz = s.z + group.position.z;
        if (Math.hypot(sx - x, sz - z) <= radius) out.push({ x: sx, z: sz, name: s.name, halt: s.halt });
      }
    }
    return out;
  }

  /** The garage doors in the tiles now loaded within `radius` of scene (x, z): the middle of each door wall, in scene coordinates, the way it faces and how long it is. */
  garagesNear(x: number, z: number, radius: number): Array<{ x: number; z: number; faceX: number; faceZ: number; span: number }> {
    const out: Array<{ x: number; z: number; faceX: number; faceZ: number; span: number }> = [];
    for (const { group, garages } of this.vectorChunks.values()) {
      for (const g of garages) {
        const gx = g.x + group.position.x, gz = g.z + group.position.z;
        if (Math.hypot(gx - x, gz - z) <= radius) out.push({ ...g, x: gx, z: gz });
      }
    }
    return out;
  }

  /** Every helipad in the tiles now loaded, in scene coordinates. */
  helipads(): HelipadRef[] {
    const out: HelipadRef[] = [];
    for (const [chunk, { group, helipads }] of this.vectorChunks) {
      helipads.forEach((p, i) => out.push({
        id: `${chunk}#${i}`, x: p.x + group.position.x, y: p.y, z: p.z + group.position.z,
        yaw: p.yaw, radius: p.radius, name: p.name,
      }));
    }
    return out;
  }

  /** The nearest parked vehicle within `radius` of scene (x, z), if any. */
  nearestParked(x: number, z: number, radius: number,
    accept?: (cx: number, cz: number, yaw: number, species: Species) => boolean): ParkedRef | undefined {
    let best: ParkedRef | undefined;
    let bestD = radius;
    for (const [chunk, { group, parts }] of this.vectorChunks) {
      const lx = x - group.position.x, lz = z - group.position.z;
      for (const mesh of parts.flora) {
        const species = mesh.name.slice('flora:'.length) as Species;
        if (!VEHICLES.has(species)) continue;
        const e = mesh.instanceMatrix.array;
        for (let i = 0; i < mesh.count; i++) {
          const o = i * 16;
          // A taken one is scaled to nothing.
          if (e[o] === 0 && e[o + 2] === 0) continue;
          const d = Math.hypot(e[o + 12] - lx, e[o + 14] - lz);
          if (d >= bestD) continue;
          const cx = e[o + 12] + group.position.x, cz = e[o + 14] + group.position.z;
          if (accept !== undefined && !accept(cx, cz, Math.atan2(e[o + 8], e[o + 10]), species)) continue;
          bestD = d;
          mesh.getColorAt(i, scratchColour);
          best = {
            chunk, mesh, index: i, species,
            x: e[o + 12] + group.position.x, y: e[o + 13], z: e[o + 14] + group.position.z,
            // The heading of its long axis, and the tilt of its two axes.
            yaw: Math.atan2(e[o + 8], e[o + 10]),
            pitch: Math.asin(Math.max(-1, Math.min(1, -e[o + 9]))),
            roll: Math.atan2(e[o + 1], e[o + 5]),
            paint: scratchColour.getHex(),
          };
        }
      }
    }
    return best;
  }

  /** Take a parked vehicle away: it stops being drawn and being solid, for good. */
  takeParked(ref: ParkedRef): void {
    const chunk = this.vectorChunks.get(ref.chunk);
    if (chunk === undefined) return;
    const x = ref.x - chunk.group.position.x, z = ref.z - chunk.group.position.z;
    let list = this.taken.get(ref.chunk);
    if (list === undefined) this.taken.set(ref.chunk, (list = []));
    list.push({ x, z });
    this.hideParked(ref.chunk, chunk.parts, x, z);
  }

  /**
   * A tailpipe on every parked car that runs on petrol or diesel — none on an
   * electric one, which is how it is told at a glance. Which is which is the
   * car's own tank, found from where it stands and its paint (see `tankOf`).
   */
  private buildExhausts(parts: VegetationChunk, group: Group): InstancedMesh | undefined {
    if (this.toGeo === undefined) return undefined;
    const matrices: number[] = [];
    const cars: number[] = [];
    const offset = new Matrix4();
    for (const mesh of parts.flora) {
      const species = mesh.name.slice('flora:'.length) as Species;
      const kind = CAR_KIND[species];
      if (kind === undefined) continue;
      offset.makeTranslation(exhaustSpot(kind));
      const e = mesh.instanceMatrix.array;
      for (let i = 0; i < mesh.count; i++) {
        const o = i * 16;
        if (e[o] === 0 && e[o + 2] === 0) continue;
        // The same sums as `nearestParked`, so the same car is the same tank.
        const geo = this.toGeo(e[o + 12] + group.position.x, -(e[o + 14] + group.position.z));
        mesh.getColorAt(i, scratchColour);
        if (tankOf(species as VehicleSpecies, scratchColour.getHex(), geo.lat, geo.lon).energy === 'electric') continue;
        mesh.getMatrixAt(i, scratchMatrix);
        scratchMatrix.multiply(offset);
        matrices.push(...scratchMatrix.elements);
        cars.push(e[o + 12], e[o + 14]);
      }
    }
    if (cars.length === 0) return undefined;
    const pipes = new InstancedMeshClass(exhaustGeometry, exhaustMaterial, cars.length / 2);
    pipes.instanceMatrix.array.set(matrices);
    pipes.instanceMatrix.needsUpdate = true;
    pipes.userData.cars = new Float32Array(cars);
    pipes.computeBoundingSphere();
    pipes.castShadow = false;
    return pipes;
  }

  private hideParked(id: string, parts: VegetationChunk, x: number, z: number): void {
    const pipes = parts.exhausts;
    if (pipes !== undefined) {
      const cars = pipes.userData.cars as Float32Array;
      for (let i = 0; i < cars.length / 2; i++) {
        if (Math.hypot(cars[i * 2] - x, cars[i * 2 + 1] - z) > 0.3) continue;
        pipes.setMatrixAt(i, ZERO);
        pipes.instanceMatrix.needsUpdate = true;
      }
    }
    for (const mesh of parts.flora) {
      const species = mesh.name.slice('flora:'.length) as Species;
      if (!VEHICLES.has(species)) continue;
      for (let i = 0; i < mesh.count; i++) {
        mesh.getMatrixAt(i, scratchMatrix);
        const e = scratchMatrix.elements;
        if (Math.hypot(e[12] - x, e[14] - z) > 0.3) continue;
        // An articulated truck's trailer is solid as a box of its own, behind it.
        if (species === 'truckSemi') {
          const back = Math.hypot(e[8], e[10]) || 1;
          this.solid.get(id)?.index.disableNear(x + (e[8] / back) * SEMI_TRAILER_BOX.behind, z + (e[10] / back) * SEMI_TRAILER_BOX.behind, 0.3);
        }
        mesh.setMatrixAt(i, ZERO);
        mesh.instanceMatrix.needsUpdate = true;
      }
    }
    this.solid.get(id)?.index.disableNear(x, z, 0.3);
  }

  /** Give a terrain chunk its tile's landcover, if both are present. */
  private linkLandcover(id: string): void {
    const chunk = this.chunks.get(id);
    const texture = this.landcover.get(id);
    if (chunk === undefined || texture === undefined || chunk.own !== undefined) return;
    const landcover: TerrainLandcover = { texture, width: chunk.width, depth: chunk.depth };
    // A material per chunk for its uniforms; the program behind it is shared,
    // because every terrain material compiles from the same source.
    chunk.own = createTerrainMaterial({ landcover });
    for (const piece of chunk.pieces) piece.material = chunk.own;
  }

  /**
   * The landcover class at scene (x, z), from its tile's raster (see
   * `LANDCOVER` in core/sources/shortbread.ts); 0 where there is none.
   */
  landcoverAt(x: number, z: number): number {
    for (const [id, texture] of this.landcover) {
      const chunk = this.chunks.get(id);
      if (chunk === undefined) continue;
      // The raster runs from the tile's north-west corner, where its mesh is placed (`place`).
      const lx = x - chunk.mesh.position.x, lz = z - chunk.mesh.position.z;
      if (lx < 0 || lz < 0 || lx >= chunk.width || lz >= chunk.depth) continue;
      const size = texture.image.width;
      const data = texture.image.data as Uint8Array;
      return data[Math.floor((lz / chunk.depth) * size) * size + Math.floor((lx / chunk.width) * size)];
    }
    return 0;
  }

  /**
   * The terrain pieces that wear a landcover raster, each with it: for the
   * snow, which keeps off car parks (see snowField.ts).
   */
  *landcoverPieces(): Generator<{ mesh: Mesh; landcover: TerrainLandcover }> {
    for (const [id, chunk] of this.chunks) {
      const texture = this.landcover.get(id);
      if (texture === undefined || chunk.own === undefined) continue;
      const landcover = { texture, width: chunk.width, depth: chunk.depth };
      for (const mesh of chunk.pieces) if (mesh.parent !== null) yield { mesh, landcover };
    }
  }

  private dropLandcover(id: string): void {
    const texture = this.landcover.get(id);
    if (texture === undefined) return;
    this.landcover.delete(id);
    texture.dispose();
    const chunk = this.chunks.get(id);
    if (chunk?.own !== undefined) {
      for (const piece of chunk.pieces) piece.material = this.material;
      chunk.own.dispose();
      chunk.own = undefined;
    }
  }

  clear(): void {
    for (const key of [...this.chunks.keys()]) {
      const chunk = this.chunks.get(key)!;
      this.scene.remove(chunk.mesh);
      for (const piece of chunk.pieces) piece.geometry.dispose();
      chunk.own?.dispose();
    }
    this.chunks.clear();
    for (const texture of this.landcover.values()) texture.dispose();
    this.landcover.clear();
    for (const key of [...this.vectorChunks.keys()]) {
      const chunk = this.vectorChunks.get(key)!;
      this.scene.remove(chunk.group);
      disposeVegetation(chunk.parts);
      this.footpaths.remove(key);
      this.lanes.remove(key);
      this.tracks.remove(key);
      this.railways.remove(key);
      this.crossings.remove(key);
      this.walkable.delete(key);
      this.solid.delete(key);
    }
    this.vectorChunks.clear();
    this.refreshLampMap();
  }

  private refreshLampMap(): void {
    const chunks: Array<{ x: number; z: number; lamps: Float32Array | undefined }> = [];
    for (const { group, parts } of this.vectorChunks.values()) {
      chunks.push({
        x: group.position.x,
        z: group.position.z,
        lamps: parts.pools?.userData.lamps as Float32Array | undefined,
      });
    }
    updateLampLightMap(chunks);
  }

  /**
   * Position a chunk's north-west corner in the scene.
   *
   * Scene axes match the core's convention exactly — X east, Y up, Z south —
   * so the only conversion is that a chunk `originNorth` metres north of the
   * origin sits at negative Z.
   *
   * The position is always re-derived from the tile key against the *current*
   * origin, never taken from the mesh payload: the worker computed those
   * fields against whatever origin was current when the tile was requested, and
   * a rebase while the tile was in flight would leave them a frame behind.
   */
  private place(object: Object3D, key: TileKey): void {
    if (this.origin === undefined) return;
    const extent = tileExtent(key, this.origin);
    object.position.set(extent.minEast, 0, -extent.maxNorth);
    object.updateMatrix();
  }
}
